import request from '../../../util/SuperagentUtils';
import { citiesRequests , locationRequests, supplierBookingsReportEndpoints} from '../../../requests/api-endpoints';
import type { CapacityChange, LocationAvailabilityCalendar, LocationCapacityScheduleRow } from '../../../types/LocationCapacitySchedule';
import { LocationDeactivateVO } from '../../../types/model/valueObject/location/LocationDeactivateVO';
import type { SupplierLocationListingVO } from '../../../types/model/valueObject/location/SupplierLocationListingVO';

export const enrichLocation = (address: string): Promise<any> => {
  // Enrichment latency is typically 5–6s (Gemini generation); the API contract
  // recommends a client timeout of at least 25s.
  return request.post(locationRequests.enrichment).timeout(25000).send({address});
}

export const enrichLocationByLatLng = (latitude: number, longitude: number): Promise<any> => {
  // Enrichment latency is typically 5–6s (Gemini generation); the API contract
  // recommends a client timeout of at least 25s.
  return request.post(locationRequests.enrichment).timeout(25000).send({latitude, longitude});
}

export const requestLocations = (): Promise<any> => {
  return request.get(locationRequests.location);
}

export const searchSupplierLocations = (): Promise<any> => {
  return request.get(locationRequests.searchSupplierLocations);
}

/**
 * Fetches a single location with full detail for the edit wizard.
 *
 * @param locationId The id of the location to load
 * @returns Promise resolving with the full LocationFullVO
 */
export const getLocationForEdit = (locationId: string): Promise<any> => {
  return request.get(locationRequests.locationGet(locationId));
}

/**
 * Creates a new location draft (status DRAFT, live = false). Used by the
 * Create/Edit Location wizard on the first save. The backend accepts partial
 * data, so only the fields collected so far need be supplied.
 *
 * @param location Partial location payload
 * @returns Promise resolving with the created location (server-assigned id, etc.)
 */
export const createLocationDraft = (location: any): Promise<any> => {
  return request.post(locationRequests.locationDraft).send(location);
}

/**
 * Updates an existing location via PATCH. Used by the Create/Edit Location
 * wizard for all saves after the draft has been created.
 *
 * @param locationId The id of the location to update
 * @param location Partial location payload with the fields to change
 */
export const updateLocation = (locationId: string, location: any): Promise<any> => {
  return request.patch(locationRequests.locationUpdate(locationId)).send(location);
}

/**
 * Persists the gallery image file names associated with a saved location.
 *
 * @param location The saved location record (must include an id)
 * @param galleryFiles Array of uploaded gallery file names to associate
 */
export const saveLocationGallery = (location: any, galleryFiles: string[]): Promise<any> => {
  return request.post(`${locationRequests.location}/gallery`).send({ location, galleryFiles });
}

export const getActiveBookingsForLocation = (locationId: string): Promise<any> => {
  return request.get(supplierBookingsReportEndpoints.search)
    .query({ locationId, activeStatus: 'ACTIVE', size: 1 });
}

/**
 * Counts the supplier's currently active bookings, optionally for one location.
 *
 * Asks for a single row because only the page `count` is used — the endpoint is
 * a paginated search, so this avoids pulling every booking back to total them
 * on the client. Omitting `locationId` counts across the whole account.
 */
export const countActiveBookings = (locationId?: string): Promise<any> => {
  const query: Record<string, string | number> = { activeStatus: 'ACTIVE', size: 1 };
  if (locationId) query.locationId = locationId;
  return request.get(supplierBookingsReportEndpoints.search).query(query);
}

/**
 * Deletes a location by id. Supported for DRAFT and DEACTIVATED locations.
 * Uses `DELETE /api/locations/:locationId` (path param).
 *
 * @param locationId - The id of the location to delete
 * @returns Promise resolving with the server response (empty body if successful)
 */
export const deleteLocation = (locationId: string): Promise<any> => {
  return request.delete(locationRequests.locationDelete(locationId));
}

/**
 * Submits a draft location for SecurSpace admin review or requests reactivation
 * for a deactivated location. Posts directly to the review endpoint without a
 * client-side location fetch or update.
 *
 * @param locationId The id of the location to submit or reactivate
 */
export const submitLocationForReview = (locationId: string): Promise<any> => {
  return request.post(locationRequests.submitForReview(locationId));
}

/**
 * Clones an existing location into a new DRAFT copy. The backend creates a new
 * location named `Copy-{originalName}` with status DRAFT (live = false) and
 * returns the newly created location.
 *
 * @param locationId The id of the location to clone
 * @returns Promise resolving with the newly created draft location
 */
export const cloneLocation = (locationId: string): Promise<any> => {
  return request.post(locationRequests.clone(locationId));
}

/**
 * Takes a location live (admin only). Sets `live = true` and `status = APPROVED`
 * server-side. Used by the wizard's admin-only Step 6 when "Location Live" is
 * toggled on, instead of a generic PATCH of `live`.
 *
 * @param locationId The id of the location to activate
 * @returns Promise resolving with the updated LocationFullVO
 */
export const activateLocation = (locationId: string): Promise<any> => {
  return request.post(locationRequests.activate(locationId));
}

/**
 * Takes a live location offline while preserving its APPROVED status
 * (`status = APPROVED`, `live = false`). Idempotent on an already-deactivated
 * APPROVED location. The backend rejects (400) draft/in-review locations that
 * were never live, so only call this when the location is currently live.
 *
 * @param locationId The id of the location to deactivate
 * @returns Promise resolving with the updated LocationFullVO
 */
export const deactivateLocation = (locationId: string, locationDeactivateVO: LocationDeactivateVO): Promise<{body: SupplierLocationListingVO}> => {
  return request.post(locationRequests.deactivate(locationId)).send(locationDeactivateVO);
}

export const getLocationManagers = (accountId: string): Promise<any> => {
  return request.get(locationRequests.locationManagers.get(accountId));
}

export const getLocationById = (locationId: string): Promise<any> => {
  return request.get(locationRequests.locationById(locationId));
}

export const getLocationByIdWithDates = (locationId: string, startDate: string, endDate: string): Promise<any> => {
  return request.get(locationRequests.locationById(locationId)).query({startDate: startDate, endDate: endDate});
}

export const getLocationHasRequestedCapacityById = (locationId: string, numberOfSpaces: number, startDate: string, endDate: string, durationType?: string): Promise<any> => {
  return request
    .get(locationRequests.locationCapacityById(locationId))
    .query({
      numberOfSpaces,
      startDate,
      endDate,
      ...(durationType && { durationType }),
    });
};

/** Per-day availability for the booking calendar; dates are YYYY-MM-DD, inclusive, at most 62 days. */
export const getLocationAvailabilityCalendar = (locationId: string, startDate: string, endDate: string): Promise<{body: LocationAvailabilityCalendar}> => {
  return request
    .get(locationRequests.locationAvailabilityById(locationId))
    .query({startDate, endDate});
};

export const getLocationSuppliers = (optimizedLocationSearch: boolean, latitude: string, longitude: string, startDate: string, endDate: string, numberOfSpaces: number): Promise<any> => {
  return optimizedLocationSearch ?
    request.get(locationRequests.latLongSearch + `?lat=${latitude}&lon=${longitude}`)
  :
    request.get(locationRequests.latLongSearchLegacy + `?lat=${latitude}&lon=${longitude}&startDate=${startDate}&endDate=${endDate}&numberOfSpaces=${numberOfSpaces}`);
};

export const getLocationSuppliersInRadius = (optimizedLocationSearch: boolean, latitude: string, longitude: string, mileRadius: number, startDate: string, endDate: string, numberOfSpaces: number): Promise<any> => {
  return optimizedLocationSearch ?
    request.get(locationRequests.latLongSearch + `?lat=${latitude}&lon=${longitude}&radius=${mileRadius}`)
  :
    request.get(locationRequests.latLongSearchLegacy + `?lat=${latitude}&lon=${longitude}&radius=${mileRadius}&startDate=${startDate}&endDate=${endDate}&numberOfSpaces=${numberOfSpaces}`);
};

export const updateLocationManager = (accountId: string, updatedUser: any, onSuccess?: (response: Object)=> void,
                                      onFail?: (err: Object)=> void): (Promise<any> | void) => {

  const result = request.put(locationRequests.locationManagers.put(accountId)).send(updatedUser);

  if (onSuccess && onFail) {
    result
    .then((resp: {body: Object}) => onSuccess(resp.body))
    .catch((err: Object) => onFail(err))
  } else {
    return result
  }
}

export const getRecentlyBooked = (pageNumber: number, size: number): Promise<any> => {

  let searchParams;

  if(pageNumber || size) {
    searchParams = new URLSearchParams();

    if (pageNumber) {
      searchParams.append("pageNumber", pageNumber.toString());
    }
    if (size) {
      searchParams.append("size", size.toString());
    }
  }

  return request.get(`${locationRequests.featuredLocations}${searchParams ? `?${searchParams}`: ""}`)
}

export const getCities = (sortBy: string, sortDir: string, page: number, size: number): Promise<any> => {
  const searchParams = new URLSearchParams();
  searchParams.append("sortBy", sortBy);
  searchParams.append("sortDir", sortDir);
  searchParams.append("page", page.toString());
  searchParams.append("size", size.toString());
  return request.get(`${citiesRequests.search}?${searchParams}`);
}

export const getByIpLocation = (): Promise<any> => {
  return request.get(locationRequests.locationByIpAddress);
};

export const getAllLocationsByBuyer = (): Promise<any> => {
  return request.get(locationRequests.locationsByBuyer);
};

export const getAllBuyerFavoriteLocations = (): Promise<any> => {
  return request.get(locationRequests.getFavorites);
};

/**
 * Get all currently logged-in user's locations. Response consists of id and locationName only.
 * @returns {Promise}
 */
export const getAllLocationNames = (): Promise<any> => {
  return request.get(locationRequests.locationNames);
};

export const getIsFavoriteLocation = (locationId: string): Promise<any> => {
  return request.get(locationRequests.favorite.get(locationId)).timeout({ response: 10000, deadline: 15000 });
};

export const setFavoriteLocation = (locationId: string, isFavorite: boolean): Promise<any> => {
  return isFavorite
    ? request.put(locationRequests.favorite.create(locationId)).timeout({ response: 10000, deadline: 15000 })
    : request.delete(locationRequests.favorite.remove(locationId)).timeout({ response: 10000, deadline: 15000 });
};

export const getBookedLocations = (query: {city?: string, state?: string, supplierCompanyName?: string, equipmentType?: string, sortBy?: string, sortDir?: string}, page: number, size: number): Promise<any> => {
  const searchParams = new URLSearchParams();
  if (query.city) searchParams.append("city", query.city);
  if (query.state) searchParams.append("state", query.state);
  if (query.supplierCompanyName) searchParams.append("supplierCompanyName", query.supplierCompanyName);
  if (query.equipmentType) searchParams.append("equipmentType", query.equipmentType);
  if (page >= 0) searchParams.append("page", page.toString());
  if (size >= 0 ) searchParams.append("size", size.toString());
  if (query.sortBy) searchParams.append("sortBy", query.sortBy);
  if (query.sortDir) searchParams.append("sortDir", query.sortDir);

  return request.get(`${locationRequests.booked}?${searchParams}`);
};

// --- POC: supplier effective-dated capacity schedule ---

const CAPACITY_SCHEDULE_VERSION_HEADER = 'X-Capacity-Schedule-Version';

/** The schedule version from a GET/PUT capacity-schedule response, or null when absent. */
export const getCapacityScheduleVersion = (response: any): number | null => {
  const version = Number(response?.headers?.[CAPACITY_SCHEDULE_VERSION_HEADER.toLowerCase()]);
  return Number.isInteger(version) ? version : null;
};

/** Fetch the effective-dated capacity rows for a location. */
export const getCapacitySchedule = (locationId: string): Promise<any> => {
  return request.get(locationRequests.capacitySchedule(locationId));
};

/**
 * Replace the entire capacity schedule (calendar/table editor save). The API requires `expectedVersion` and rejects
 * the save with CAPACITY_SCHEDULE_CONFLICT if someone else saved since that version was loaded.
 */
export const replaceCapacitySchedule = (
  locationId: string,
  rows: LocationCapacityScheduleRow[],
  expectedVersion?: number | null,
): Promise<any> => {
  const put = request.put(locationRequests.capacitySchedule(locationId));
  if (expectedVersion != null) {
    put.set(CAPACITY_SCHEDULE_VERSION_HEADER, String(expectedVersion));
  }
  return put.send(rows);
};

/** Read-only check of whether a capacity change drops below confirmed bookings. Nothing is saved. */
export const assessCapacityReduction = (locationId: string, change: CapacityChange): Promise<any> => {
  return request.post(locationRequests.capacityScheduleImpact(locationId)).send(change);
};

/**
 * Bulk-upload a CSV / Excel-exported CSV of capacity rows. Like the editor save, the API rejects it with
 * CAPACITY_SCHEDULE_CONFLICT if someone else saved since `expectedVersion` was loaded.
 */
export const uploadCapacitySchedule = (
  locationId: string,
  file: File,
  expectedVersion?: number | null,
): Promise<any> => {
  const post = request.post(locationRequests.capacityScheduleUpload(locationId));
  if (expectedVersion != null) {
    post.set(CAPACITY_SCHEDULE_VERSION_HEADER, String(expectedVersion));
  }
  return post.attach('file', file);
};

/** URL for downloading the CSV upload template. */
export const capacityTemplateUrl = (locationId: string): string => {
  return locationRequests.capacityScheduleTemplate(locationId);
};

/** Bulk-upload one CSV covering capacity schedules for many locations. */
export const uploadBulkCapacitySchedule = (file: File): Promise<any> => {
  return request.post(locationRequests.bulkCapacityScheduleUpload).attach('file', file);
};

/** URL for the global template, pre-filled with the supplier's location ids/names. */
export const bulkCapacityTemplateUrl = (): string => {
  return locationRequests.bulkCapacityScheduleTemplate;
};

/**
 * Fetches the bulk location edit export as raw CSV text, rather than triggering
 * a browser download. Used by the assistant, which edits the export in memory
 * and posts it back to the validate/apply endpoints.
 *
 * @returns Promise resolving with the CSV body as a string
 */
export const fetchBulkLocationEditExportCsv = async (): Promise<string> => {
  const response: any = await request.get(locationRequests.bulkLocationEditExport).buffer(true);
  if (typeof response.text === 'string' && response.text.length > 0) {
    return response.text;
  }
  // Some browsers hand superagent a Blob even for text/csv.
  if (response.body instanceof Blob) {
    return response.body.text();
  }
  return '';
};

/**
 * Validate an edited locations CSV. This is a dry run — the server saves
 * nothing and returns a preview of what would change.
 */
export const validateBulkLocationEdit = (file: File): Promise<any> => {
  return request.post(locationRequests.bulkLocationEditValidate).attach('file', file);
};

/**
 * Apply an edited locations CSV. All-or-nothing: the server re-validates and
 * writes nothing if any row fails.
 */
export const applyBulkLocationEdit = (file: File): Promise<any> => {
  return request.post(locationRequests.bulkLocationEditApply).attach('file', file);
};
